// src/components/common/CountryMultiSelect.jsx
import React, { useMemo, useState, Fragment } from "react";
import Select, { components as RS } from "react-select";
import countryList from "react-select-country-list";
import { Controller } from "react-hook-form";
import { Dialog, Transition } from "@headlessui/react";

const compactStyles = {
  control: (b) => ({ ...b, minHeight: 34 }),
  valueContainer: (b) => ({ ...b, padding: "0 8px" }),
  indicatorsContainer: (b) => ({ ...b, padding: 2 }),
  menuPortal: (b) => ({ ...b, zIndex: 50 }),
};

const SummaryValueContainer = (props) => {
  const count = props.getValue().length;
  return (
    <RS.ValueContainer {...props}>
      {count ? <span className="text-xs text-gray-600">{count} selected</span> : props.children}
    </RS.ValueContainer>
  );
};

export default function CountryMultiSelect({ name, control, disabledValues = [] }) {
  const options = useMemo(() => countryList().getData(), []);
  const labelByIso = useMemo(
    () => Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options]
  );
  const [open, setOpen] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const selected = options.filter((o) => field.value?.includes(o.value));
        const removeIso = (iso) =>
          field.onChange((field.value || []).filter((v) => v !== iso));

        return (
          <>
            <div className="flex items-center gap-2">
              <div className="grow">
                <Select
                  {...field}
                  isMulti
                  options={options}               // shows full country names
                  classNamePrefix="rs"
                  styles={compactStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  placeholder="Select countries…"
                  controlShouldRenderValue={false}
                  components={{ ValueContainer: SummaryValueContainer }}
                  isOptionDisabled={(opt) =>
                    disabledValues.includes(opt.value) &&
                    !(field.value || []).includes(opt.value)
                  }
                  onChange={(vals) => field.onChange(vals.map((v) => v.value))}
                  value={options.filter((o) => field.value?.includes(o.value))}
                />
              </div>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                title="View / remove selected countries"
              >
                Manage{selected.length ? ` (${selected.length})` : ""}
              </button>
            </div>

            {/* Manage dialog */}
            <Transition as={Fragment} show={open}>
              <Dialog onClose={() => setOpen(false)} className="relative z-50">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
                  leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black/20" />
                </Transition.Child>
                <div className="fixed inset-0 grid place-items-center p-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                    leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-md rounded bg-white p-4 shadow">
                      <Dialog.Title className="text-sm font-medium mb-2">
                        Selected countries
                      </Dialog.Title>

                      {selected.length === 0 ? (
                        <p className="text-xs text-gray-500">None selected.</p>
                      ) : (
                        <ul className="max-h-64 overflow-auto divide-y">
                          {selected.map((o) => (
                            <li key={o.value} className="flex items-center justify-between py-2 text-sm">
                              <span>{o.label}</span>
                              <button
                                type="button"
                                onClick={() => removeIso(o.value)}
                                className="text-red-600 text-xs hover:underline"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="mt-3 flex justify-between">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="text-xs px-3 py-1 rounded border hover:bg-gray-50"
                        >
                          Close
                        </button>
                        {!!selected.length && (
                          <button
                            type="button"
                            onClick={() => field.onChange([])}
                            className="text-xs px-3 py-1 rounded border text-red-600 hover:bg-red-50"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </Dialog>
            </Transition>
          </>
        );
      }}
    />
  );
}
